import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { OverlayService } from '@app/shared/components/overlay/overlay.service';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { OrganizationStructureService } from '@app/modules/organization-structure/services/organization-structure.service';
import { SnackbarService } from '@app/core/services/snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import { api_routes, db_tables } from '@app/consts';
import { OrganizationType, OrganizationTypeVerbose } from '../../models/organization-structure.model';
import { FormGeneratorDialogComponent } from '@app/shared/components/form-generator-dialog/form-generator-dialog.component';
import { GridState } from '@app/shared/services/grid-state/grid-state.model';
import { ConfirmDialogComponent } from '@app/shared/components/confirm-dialog/confirm-dialog.component';
import { defer, forkJoin } from 'rxjs';

@Component({
    selector: 'add-user-dialog',
    templateUrl: './organization-types-dialog.component.html',
    styleUrls: ['./organization-types-dialog.component.scss']
})
export class OrganizationTypesDialogComponent implements OnInit {
    public columns: any[] = [
        { field: "name", title: "Name", localizationCode: "OrganizationTypeName" },
        { field: "version", subField: "createdOn", title: "Created On", localizationCode: "OrganizationTypeCreatedOn", type: "date" },
    ];

    sortableColumns: any[] = [
        { field: "name", sortValue: "name" },
        { field: "version", sortValue: "version.createdOn" }
    ];

    public gridDataResult: GridDataResult;
    public api_routes = api_routes;
    public db_tables = db_tables;
    isLoading: boolean = true;
    pageSize: number = 20;
    skip: number = 0;
    clearSelectedItems: boolean = false;
    formValid: any;
    sortString: string = 'name-desc';
    isLoadingPermissions: boolean = true;
    filterString: string;
    gridId: string = 'organizationTypesGrid';
    getRequest: any;
    searchValue: string;
    searchFilterString: string;
    formId: string = 'frm_pqVcDGegIGsdjT';
    formGeneratorDialogRef: MatDialogRef<FormGeneratorDialogComponent>;


    constructor(
        private dialog: MatDialog,
        private overlayService: OverlayService,
        private snackbarService: SnackbarService,
        private dialogRef: MatDialogRef<OrganizationTypesDialogComponent>,
        public translate: TranslateService,
        private organizationService: OrganizationStructureService,
        @Inject(MAT_DIALOG_DATA) data
    ) {

    }

    ngOnInit(): void {
        this.getOrganizationTypes();
    }

    getOrganizationTypes() {
        this.isLoading = true;

        this.getRequest = this.organizationService.getOrganizationTypes(this.pageSize, this.skip, this.searchFilterString, this.sortString)
            .subscribe({
                next: (res) => {
                    this.gridDataResult = {
                        data: res.data,
                        total: res.total
                    };
                    this.isLoading = false;
                },
                error: (err) => {
                    this.isLoading = false;
                }
            });
    }

    gridStateChange(gridState: GridState) {
        if (this.pageSize !== gridState.pageSize) {
            this.pageSize = gridState.pageSize;
        }
        if (this.skip !== gridState.skip) {
            this.skip = gridState.skip;
        }
        if (this.sortString !== gridState.sortString) {
            this.sortString = gridState.sortString;
        }
        if (this.filterString !== gridState.filter) {
            this.filterString = gridState.filter;
            this.skip = 0;
        }

        if (this.searchValue !== gridState.searchValue) {
            let variants = [];
            this.searchFilterString = '';
            this.searchValue = gridState.searchValue;

            if (this.searchValue !== null) {
                variants = this.searchValue.split(' ').filter(value => value);
            }

            variants.map(variant => {
                if (variant) {
                    if (this.searchFilterString.length > 2) {
                        this.searchFilterString += ' AND ';
                    }
                    this.searchFilterString += `(Name like "${variant}")`;
                }
            });

            this.skip = 0;
        }

        this.getRequest.unsubscribe();
        this.getOrganizationTypes();
    }

    editOrCreateOrgType(orgType?: OrganizationType) {
        if (orgType) {
            this.isLoading = true;

            this.organizationService.getOrganizationType(orgType.id)
                .subscribe({
                    next: (res) => {
                        this.isLoading = false;
                        this.openForm(res);
                    },
                    error: (err) => {
                        this.isLoading = false;
                    }
                });
        } else {
            this.openForm();
        }
    }

    openForm(orgType?: OrganizationTypeVerbose) {
        let formData = {
            name: orgType ? orgType.name : null,
            id: orgType ? orgType.id : null
        }

        const dialogConfig = new MatDialogConfig();

        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = true;

        dialogConfig.data = {
            formId: this.formId,
            formData: formData,
        };

        this.formGeneratorDialogRef = this.dialog.open(FormGeneratorDialogComponent, dialogConfig);

        const sub = this.formGeneratorDialogRef.componentInstance.emitFormData.subscribe((event) => {
            if (orgType && orgType.id) {
                this.editOrganizationType(event);
            }
            else {
                this.createNewOrganizationType(event);
            }
        });

    }

    createNewOrganizationType(formData: any) {
        this.isLoading = true;

        let newOrgType: OrganizationTypeVerbose = {
            id: formData.id,
            name: formData.name
        }

        this.organizationService.createOrganizationType(newOrgType)
            .subscribe({
                next: (res) => {
                    this.isLoading = false;
                    this.snackbarService.openSnackBar('SavedSuccessfully', 'clear', 'success');
                    this.formGeneratorDialogRef.close();
                    this.getOrganizationTypes();
                },
                error: (err) => {
                    this.isLoading = false;
                }
            });
    }

    editOrganizationType(formData: any) {
        this.isLoading = true;

        let updatedOrgType: OrganizationTypeVerbose = {
            id: formData.id,
            name: formData.name
        }

        this.organizationService.editOrganizationType(updatedOrgType.id, updatedOrgType)
            .subscribe({
                next: (res) => {
                    this.isLoading = false;
                    this.snackbarService.openSnackBar('SavedSuccessfully', 'clear', 'success');
                    this.formGeneratorDialogRef.close();
                    this.getOrganizationTypes();
                },
                error: (err) => {
                    this.isLoading = false;
                }
            });
    }

    openConfirmDeleteDialog(orgTypeIds: string[]) {
        const dialogConfig = new MatDialogConfig();

        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = true;

        dialogConfig.data = {
            text: this.translate.instant('AreYouSureYouWantToDeleteThis')
        };

        const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig);
        dialogRef.afterClosed().subscribe(
            data => {
                if (data === true) {
                    this.deleteOrganizationTypes(orgTypeIds);
                }
            }
        );
    }

    deleteOrganizationTypes(orgTypeIds: string[]) {
        const observables = orgTypeIds.map(selectedItem => defer(() => this.organizationService.deleteOrganizationType(selectedItem)));

        this.overlayService.show();

        forkJoin(observables)
            .subscribe(
                {
                    next: (v) => {
                        this.clearSelectedItems = !this.clearSelectedItems;
                        this.snackbarService.openSnackBar(`${this.translate.instant('DeletedSuccessfully')}`, 'clear', 'success');
                        this.getOrganizationTypes();
                    },
                    error: (e) => this.overlayService.hide(),
                    complete: () => this.overlayService.hide()
                }
            );
    }

    close() {
        this.dialogRef.close();
    }


}